/* ExtraOpvangdagAanvraag — ouder vraagt extra opvangdag aan.
 * Live-list van bestaande verzoeken + inline formulier.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import DateField from "@/components/DateField";
import { toast } from "sonner";
import { Loader2, CalendarPlus, CheckCircle2, XCircle, Clock } from "lucide-react";

const _DAGDELEN = [
  { v: "hele_dag", l: "Hele dag" },
  { v: "ochtend", l: "Ochtend" },
  { v: "middag", l: "Middag" },
];

const _STATUS_STYLE = {
  aangevraagd: { cls: "bg-amber-100 text-amber-800", label: "Wacht op goedkeuring", icon: Clock },
  goedgekeurd: { cls: "bg-emerald-100 text-emerald-800", label: "Goedgekeurd", icon: CheckCircle2 },
  afgewezen: { cls: "bg-red-100 text-red-800", label: "Afgewezen", icon: XCircle },
  geannuleerd: { cls: "bg-slate-100 text-slate-700", label: "Geannuleerd", icon: XCircle },
};

export default function ExtraOpvangdagAanvraag({ kindId, kindNaam }) {
  const today = new Date().toISOString().slice(0, 10);
  const [datum, setDatum] = useState("");
  const [dagdeel, setDagdeel] = useState("hele_dag");
  const [reden, setReden] = useState("");
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyError, setHistoryError] = useState(null);

  const load = async () => {
    setLoading(true);
    setHistoryError(null);
    try {
      const r = await api.get("/portaal/extra-opvangdagen");
      setItems((r.data || []).filter(x => !kindId || x.kind_id === kindId));
    } catch (e) {
      setHistoryError(e?.response?.data?.detail || "Kon geschiedenis niet laden");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kindId]);

  const submit = async () => {
    if (!datum) { toast.error("Kies een datum"); return; }
    setBusy(true);
    try {
      await api.post(`/portaal/kind/${kindId}/extra-opvangdag`, { datum, dagdeel, reden });
      toast.success("Verzoek ingediend — je krijgt bericht bij goedkeuring");
      setDatum(""); setReden("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aanvraag mislukt");
    } finally { setBusy(false); }
  };

  return (
    <Card className="rounded-2xl border border-neutral-200 bg-white p-4"
          data-testid="extra-opvangdag-aanvraag-card">
      <div className="flex items-center gap-2 mb-3">
        <CalendarPlus className="h-4 w-4 text-[color:var(--terracotta)]" />
        <h3 className="font-display text-lg tracking-tight">Extra opvangdag aanvragen</h3>
      </div>
      <p className="text-xs text-neutral-600 mb-3">
        Vraag voor <strong>{kindNaam}</strong> een extra dag aan. Je krijgt direct antwoord
        zodra de manager de aanvraag beoordeelt (BKR wordt automatisch gecheckt).
      </p>
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <div className="w-44">
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Datum</label>
          <DateField value={datum} min={today} onChange={setDatum}
                     data-testid="extra-opvangdag-datum" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Dagdeel</label>
          <select value={dagdeel} onChange={e => setDagdeel(e.target.value)}
                  data-testid="extra-opvangdag-dagdeel"
                  className="h-9 border border-neutral-200 rounded px-2 text-sm">
            {_DAGDELEN.map(d => <option key={d.v} value={d.v}>{d.l}</option>)}
          </select>
        </div>
        <div className="min-w-[180px] flex-1">
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Reden (optioneel)</label>
          <Input value={reden} onChange={e => setReden(e.target.value)}
                 placeholder="Bijv. werkverplichting"
                 className="h-9" data-testid="extra-opvangdag-reden" />
        </div>
        <Button size="sm" onClick={submit} disabled={busy}
                style={{ background: "var(--sage)" }} className="h-9 text-white"
                data-testid="extra-opvangdag-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aanvragen"}
        </Button>
      </div>
      {loading && (
        <div className="text-xs text-neutral-500 flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" /> Bestaande verzoeken laden …
        </div>
      )}
      {!loading && historyError && (
        <div className="flex items-center justify-between gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800"
             data-testid="extra-opvangdag-history-error">
          <span>Kon je aanvragen niet laden — {historyError}</span>
          <Button size="sm" variant="outline" onClick={load}
                  data-testid="extra-opvangdag-history-retry" className="h-7 text-xs">
            Opnieuw
          </Button>
        </div>
      )}
      {!loading && items.length > 0 && (
        <div className="mt-2 border-t border-neutral-100 pt-3"
             data-testid="extra-opvangdag-history">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
            Recente aanvragen
          </div>
          <ul className="space-y-1.5">
            {items.slice(0, 5).map(r => {
              const st = _STATUS_STYLE[r.status] || _STATUS_STYLE.aangevraagd;
              const Icon = st.icon;
              return (
                <li key={r.id} className="flex items-center gap-2 text-xs"
                    data-testid={`extra-opvangdag-history-${r.id}`}>
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${st.cls}`}>
                    <Icon className="h-3 w-3" /> {st.label}
                  </span>
                  <span className="text-neutral-700">{r.datum} · {r.dagdeel}</span>
                  {r.reden_afwijzing && (
                    <span className="text-red-700 italic truncate">
                      — {r.reden_afwijzing}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Card>
  );
}
